import { BASE_URL } from "../config";

export function normalizeRoundingMode(value) {
  const mode = String(value || "nearest").trim().toLowerCase();
  const normalizedMode = mode.replace(/[\s-]+/g, "_");
  if (
    ["up", "increase", "ceil", "round_up", "roundup", "round_up_increase"].includes(
      normalizedMode,
    )
  ) {
    return "up";
  }
  if (
    [
      "down",
      "decrease",
      "floor",
      "round_down",
      "rounddown",
      "round_down_decrease",
    ].includes(normalizedMode)
  ) {
    return "down";
  }
  return "nearest";
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function unwrapShiftSettings(payload) {
  return (
    payload?.data?.shiftSettings ||
    payload?.data ||
    payload?.shiftSettings ||
    payload ||
    {}
  );
}

function isEnabledValue(value) {
  if (value === true) return true;
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "true") return true;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
}

export function normalizeShiftRoundingSettings(payload) {
  const data = unwrapShiftSettings(payload);
  const graceTime = Number(firstValue(data?.grace_time, data?.graceTime));
  const roundingRaw = firstValue(data?.grace_time_rounding, data?.graceTimeRounding);
  const intervalMinutes = Number(roundingRaw);
  const roundingEnabled = isEnabledValue(
    roundingRaw,
  );
  const enabled =
    roundingEnabled &&
    Number.isFinite(intervalMinutes) &&
    intervalMinutes > 0;

  return {
    enabled,
    intervalMinutes: enabled ? intervalMinutes : 0,
    graceWindowMinutes: Number.isFinite(graceTime) && graceTime > 0 ? graceTime : 0,
    checkInMode: normalizeRoundingMode(
      firstValue(data?.check_in_rounding, data?.checkInRounding),
    ),
    checkOutMode: normalizeRoundingMode(
      firstValue(data?.check_out_rounding, data?.checkOutRounding),
    ),
  };
}

function parseDateLike(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed =
    /[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)
      ? new Date(raw)
      : new Date(raw.replace(" ", "T") + (/\d{2}:\d{2}:\d{2}/.test(raw) ? "Z" : ""));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function applyScheduledGraceSnap(date, settings, options = {}) {
  if (!date || !settings?.enabled) return { date, snapped: false };
  const graceWindowMinutes = Number(settings.graceWindowMinutes);
  if (!Number.isFinite(graceWindowMinutes) || graceWindowMinutes <= 0) {
    return { date, snapped: false };
  }

  const scheduledValue =
    options.field === "checkin" ? options.scheduledStart : options.scheduledEnd;
  const scheduledDate = parseDateLike(scheduledValue);
  if (!scheduledDate) return { date, snapped: false };

  const diffMinutes = (date.getTime() - scheduledDate.getTime()) / 60000;
  if (options.field === "checkin" && diffMinutes >= 0 && diffMinutes <= graceWindowMinutes) {
    return { date: scheduledDate, snapped: true };
  }
  if (options.field === "checkout" && diffMinutes <= 0 && diffMinutes >= -graceWindowMinutes) {
    return { date: scheduledDate, snapped: true };
  }
  return { date, snapped: false };
}

export function roundDateByShiftSettings(date, mode, settings, options = {}) {
  if (!date || !settings?.enabled) return date;
  const time = new Date(date);
  if (Number.isNaN(time.getTime())) return date;
  const intervalMinutes = Number(settings.intervalMinutes);
  if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) return time;

  const snapResult = applyScheduledGraceSnap(time, settings, options);
  if (snapResult.snapped) return snapResult.date;
  if (intervalMinutes === 1) {
    const exactMinute = new Date(snapResult.date);
    exactMinute.setSeconds(0, 0);
    return exactMinute;
  }

  const stepMs = intervalMinutes * 60000;
  const currentMs = snapResult.date.getTime();
  const normalizedMode = normalizeRoundingMode(mode);

  if (normalizedMode === "up") {
    return new Date(Math.ceil(currentMs / stepMs) * stepMs);
  }
  if (normalizedMode === "down") {
    return new Date(Math.floor(currentMs / stepMs) * stepMs);
  }
  return new Date(Math.round(currentMs / stepMs) * stepMs);
}

export function getClientIdFromShiftLike(shiftLike) {
  return (
    shiftLike?.clientId ||
    shiftLike?.client_id ||
    shiftLike?.clientDetails?.clientId ||
    shiftLike?.clientDetails?.client_id ||
    null
  );
}

export function resolveShiftSettingsClientId({
  user,
  payload,
  shiftDetails,
  qrData,
  urlClientId,
} = {}) {
  const source = payload || user || {};
  return (
    getClientIdFromShiftLike(source?.checkedInShiftDetails) ||
    getClientIdFromShiftLike(source?.checkedInShift) ||
    getClientIdFromShiftLike(source?.currentShiftDetails) ||
    getClientIdFromShiftLike(source?.currentShift) ||
    getClientIdFromShiftLike(source?.activeShiftDetails) ||
    getClientIdFromShiftLike(source?.activeShift) ||
    getClientIdFromShiftLike(source?.scheduledShiftDetails) ||
    getClientIdFromShiftLike(source?.shiftDetails) ||
    getClientIdFromShiftLike(shiftDetails) ||
    getClientIdFromShiftLike(qrData?.shiftDetails) ||
    getClientIdFromShiftLike(qrData?.clientDetails) ||
    urlClientId ||
    null
  );
}

export async function loadClientShiftRoundingSettings(clientId) {
  if (!clientId) return null;
  try {
    const response = await fetch(
      `${BASE_URL}/api/clients/${encodeURIComponent(clientId)}/shift-settings`,
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) return null;
    return normalizeShiftRoundingSettings(payload);
  } catch (err) {
    console.warn("Failed to load client shift rounding settings", err);
    return null;
  }
}
